/**
 * F2 — Sección "Monedas" en Configuración (visual polish).
 *
 * Consume los endpoints reales de F1. Lógica, endpoints y data-testids
 * NO cambian respecto a la versión funcional aprobada.
 *
 *   GET    /api/currencies
 *   POST   /api/currencies                { code }
 *   DELETE /api/currencies/{code}
 *   PUT    /api/settings/primary-currency { code }
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Star, AlertTriangle, Loader2, Check, ChevronRight, MoreHorizontal, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Media-query hook: true cuando el viewport es < sm (640px). Evita duplicar
 * data-testids en el DOM al alternar entre acciones inline (desktop) y menú (mobile). */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 639px)').matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
}

function friendlyError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return fallback;
}

export default function CurrencySettings() {
  const isMobile = useIsMobile();
  const [state, setState] = useState({ loading: true, primary: null, enabled: [], catalog: [] });
  const [error, setError] = useState(null);
  const [busyCode, setBusyCode] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmChange, setConfirmChange] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/currencies`, { withCredentials: true });
      setState({
        loading: false,
        primary: r.data.primary,
        enabled: r.data.enabled || [],
        catalog: r.data.catalog || [],
      });
      setError(null);
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      setError(friendlyError(e, 'No pudimos cargar tus monedas. Reintentá.'));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addCurrency = async (code) => {
    setBusyCode(code);
    try {
      await axios.post(`${API}/api/currencies`, { code }, { withCredentials: true });
      toast.success(`${code} agregada`);
      setShowAdd(false);
      await refresh();
    } catch (e) {
      toast.error(friendlyError(e, `No pudimos agregar ${code}.`));
    } finally { setBusyCode(null); }
  };

  const removeCurrency = async (code) => {
    setBusyCode(code);
    try {
      await axios.delete(`${API}/api/currencies/${code}`, { withCredentials: true });
      toast.success(`${code} eliminada`);
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      const status = e?.response?.status;
      if (status === 409) {
        toast.error(detail || `No podés eliminar ${code}: tiene gastos asociados.`);
      } else if (status === 400 && /principal/i.test(detail)) {
        toast.error('No podés eliminar la moneda principal. Cambiala primero.');
      } else {
        toast.error(friendlyError(e, `No pudimos eliminar ${code}.`));
      }
    } finally { setBusyCode(null); }
  };

  const applyPrimaryChange = async (code) => {
    setBusyCode(code);
    try {
      await axios.put(`${API}/api/settings/primary-currency`, { code }, { withCredentials: true });
      toast.success(`Moneda principal actualizada a ${code}`);
      setConfirmChange(null);
      await refresh();
    } catch (e) {
      toast.error(friendlyError(e, `No pudimos cambiar la moneda principal.`));
    } finally { setBusyCode(null); }
  };

  const enabledCodes = new Set(state.enabled.map((c) => c.code));
  const addable = state.catalog.filter((c) => !enabledCodes.has(c.code));
  const primary = state.enabled.find((c) => c.code === state.primary);
  const secondaries = state.enabled.filter((c) => c.code !== state.primary);

  return (
    <div className="w-full" data-testid="currency-settings-section">
      {/* Section heading — quieter, tipografía como en una app de producto. */}
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <h4 className="text-[15px] font-semibold text-foreground tracking-tight">Monedas</h4>
          <p className="text-[12px] text-muted-foreground mt-0.5">Configurá tu moneda principal y las que usás con frecuencia.</p>
        </div>
        {state.loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      </div>

      {error && (
        <div
          data-testid="currency-load-error"
          className="mb-5 p-4 rounded-2xl bg-red-50/70 dark:bg-red-500/10 border border-red-200/70 dark:border-red-500/25 flex items-start gap-3"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" strokeWidth={2} />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-red-700 dark:text-red-300">No pudimos cargar tus monedas</p>
            <p className="text-xs text-red-600/80 dark:text-red-300/80 mt-0.5">{error}</p>
            <button
              type="button"
              onClick={refresh}
              className="mt-2 text-xs font-semibold text-red-700 dark:text-red-300 underline"
              data-testid="currency-retry-btn"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Moneda principal — hero card premium. */}
      <div className="mb-6" data-testid="primary-currency-block">
        {state.loading ? (
          <SkeletonHero />
        ) : primary ? (
          <div
            className="relative overflow-hidden rounded-[22px] p-6
                       bg-gradient-to-br from-white to-[#FEF9F1]
                       dark:from-[#12141c] dark:to-[#0d1017]
                       border border-black/[0.06] dark:border-white/[0.06]
                       shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_10px_30px_-12px_rgba(24,15,3,0.10)]
                       dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_20px_40px_-16px_rgba(0,0,0,0.5)]"
            data-testid={`primary-currency-${primary.code}`}
          >
            {/* Ambient wash — muy sutil, sólo en dark aporta profundidad */}
            <div className="pointer-events-none absolute -top-16 -right-10 w-48 h-48 rounded-full bg-amber-400/10 dark:bg-amber-500/[0.08] blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 w-40 h-40 rounded-full bg-primary/[0.06] dark:bg-primary/[0.10] blur-3xl" />

            <div className="relative flex items-center gap-5">
              <CurrencyChip code={primary.code} size="lg" tone="primary" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/80 dark:text-primary">
                    Moneda principal
                  </span>
                  <span className="w-1 h-1 rounded-full bg-primary/50" />
                  <span className="text-[10px] font-medium text-muted-foreground">Se usa en totales y conversiones</span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-2">
                  <span
                    className="text-[26px] leading-none font-semibold tracking-tight text-foreground"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {primary.code}
                  </span>
                  <span className="text-[15px] text-muted-foreground truncate">{primary.name}</span>
                </div>
              </div>
              <span
                className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em]
                           text-amber-700/90 dark:text-amber-300 bg-amber-100/70 dark:bg-amber-500/10
                           border border-amber-300/60 dark:border-amber-400/25 px-2.5 py-1 rounded-full"
                data-testid="primary-badge"
              >
                <Star className="w-3 h-3" strokeWidth={2.5} /> Principal
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Monedas habilitadas — lista limpia con divisores, sin peso administrativo */}
      <div className="mb-5">
        <div className="flex items-baseline justify-between mb-2 px-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Otras habilitadas
          </span>
          <span className="text-[11px] text-muted-foreground/70 tabular-nums">
            {secondaries.length} {secondaries.length === 1 ? 'moneda' : 'monedas'}
          </span>
        </div>

        <div
          className="rounded-[18px] bg-surface-2/60 dark:bg-white/[0.02]
                     border border-black/[0.05] dark:border-white/[0.05]"
          data-testid="enabled-currencies-list"
        >
          {secondaries.length === 0 && !state.loading && (
            <div className="px-5 py-6 text-center text-[13px] text-muted-foreground">
              Sólo tenés <span className="font-semibold text-foreground">{state.primary}</span> activa.
              <br />
              <span className="text-muted-foreground/80">Agregá otra moneda para registrar gastos en distintas divisas.</span>
            </div>
          )}
          {secondaries.map((c, idx) => {
            const isBusy = busyCode === c.code;
            return (
              <div
                key={c.code}
                className={`relative flex items-center gap-4 px-5 py-3.5 group
                           ${idx > 0 ? 'border-t border-black/[0.05] dark:border-white/[0.05]' : ''}
                           hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors`}
                data-testid={`enabled-currency-${c.code}`}
              >
                <CurrencyChip code={c.code} size="md" tone="neutral" />
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[15px] font-semibold text-foreground tracking-tight"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {c.code}
                  </div>
                  <div className="text-[12px] text-muted-foreground truncate">{c.name}</div>
                </div>

                {/* Acciones: mobile usa menú ••• para no truncar el nombre;
                    desktop mantiene layout aprobado con botones inline.
                    Renderizamos SOLO UNA de las dos ramas para evitar duplicar
                    data-testids en el DOM. */}
                {isMobile ? (
                  <RowActionMenu
                    code={c.code}
                    isBusy={isBusy}
                    onMakePrimary={() => setConfirmChange({ code: c.code, name: c.name, previous: state.primary })}
                    onRemove={() => removeCurrency(c.code)}
                  />
                ) : (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => setConfirmChange({ code: c.code, name: c.name, previous: state.primary })}
                      className="text-[12px] font-medium px-3 py-1.5 rounded-full
                                 text-muted-foreground hover:text-primary hover:bg-primary/5
                                 dark:hover:bg-primary/10 disabled:opacity-40 transition-colors"
                      data-testid={`make-primary-${c.code}-btn`}
                    >
                      Hacer principal
                    </button>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => removeCurrency(c.code)}
                      aria-label={`Eliminar ${c.code}`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground/70
                                 hover:text-red-500 hover:bg-red-500/10 disabled:opacity-40 transition-colors"
                      data-testid={`remove-currency-${c.code}-btn`}
                    >
                      {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" strokeWidth={2} />}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Agregar — botón como link premium, no bloque grande */}
      {!showAdd ? (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          disabled={state.loading || addable.length === 0}
          className="group w-full flex items-center justify-center gap-2 py-3.5 rounded-[16px]
                     border border-dashed border-primary/30 dark:border-primary/25
                     text-[13px] font-semibold text-primary
                     hover:bg-primary/[0.04] dark:hover:bg-primary/[0.08]
                     disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="add-currency-btn"
        >
          <Plus className="w-4 h-4 transition-transform group-hover:rotate-90" strokeWidth={2.5} />
          {addable.length === 0 ? 'Todas las monedas del catálogo están agregadas' : 'Agregar moneda'}
        </button>
      ) : (
        <div
          className="rounded-[18px] bg-surface-2/70 dark:bg-white/[0.02]
                     border border-black/[0.05] dark:border-white/[0.05] overflow-hidden"
          data-testid="add-currency-panel"
        >
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-black/[0.05] dark:border-white/[0.05]">
            <div>
              <p className="text-[13px] font-semibold text-foreground">Agregar moneda</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Elegí una del catálogo</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5"
              data-testid="close-add-currency-btn"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {addable.map((c, idx) => {
              const isBusy = busyCode === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  disabled={isBusy}
                  onClick={() => addCurrency(c.code)}
                  className={`relative flex items-center gap-4 w-full px-5 py-3.5 text-left
                             ${idx > 0 ? 'border-t border-black/[0.04] dark:border-white/[0.04]' : ''}
                             hover:bg-black/[0.02] dark:hover:bg-white/[0.03] disabled:opacity-40 transition-colors`}
                  data-testid={`catalog-currency-${c.code}-btn`}
                >
                  <CurrencyChip code={c.code} size="md" tone="neutral" />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[14px] font-semibold text-foreground tracking-tight"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {c.code}
                    </div>
                    <div className="text-[12px] text-muted-foreground truncate">{c.name}</div>
                  </div>
                  {isBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground/60" strokeWidth={2} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {confirmChange && (
        <ConfirmPrimaryChange
          code={confirmChange.code}
          name={confirmChange.name}
          previous={confirmChange.previous}
          busy={busyCode === confirmChange.code}
          onCancel={() => setConfirmChange(null)}
          onConfirm={() => applyPrimaryChange(confirmChange.code)}
        />
      )}
    </div>
  );
}

/**
 * RowActionMenu — Menú ••• para mobile. Preserva ambos data-testid del
 * layout desktop (make-primary-{code}-btn y remove-currency-{code}-btn)
 * para no romper tests ni automatización.
 */
function RowActionMenu({ code, isBusy, onMakePrimary, onRemove }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={isBusy}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Acciones para ${code}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground
                   hover:text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.05]
                   disabled:opacity-40 transition-colors"
        data-testid={`row-actions-${code}-btn`}
      >
        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" strokeWidth={2} />}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 min-w-[168px] z-30
                     rounded-2xl overflow-hidden
                     bg-white dark:bg-[#12141c]
                     border border-black/[0.06] dark:border-white/[0.08]
                     shadow-[0_12px_28px_-8px_rgba(24,15,3,0.18)] dark:shadow-[0_16px_32px_-12px_rgba(0,0,0,0.6)]"
          data-testid={`row-actions-menu-${code}`}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onMakePrimary(); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-medium text-left
                       text-foreground hover:bg-black/[0.03] dark:hover:bg-white/[0.05] transition-colors"
            data-testid={`make-primary-${code}-btn`}
          >
            <Star className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
            Hacer principal
          </button>
          <div className="h-px bg-black/[0.05] dark:bg-white/[0.05]" />
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onRemove(); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-medium text-left
                       text-red-600 dark:text-red-400 hover:bg-red-500/5 dark:hover:bg-red-500/10 transition-colors"
            data-testid={`remove-currency-${code}-btn`}
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={2.2} />
            Eliminar
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * CurrencyChip — Sin emojis funcionales. El código de la moneda va en el chip
 * con fuente tabular para look fintech premium. Dos tonos: primary (usado sólo
 * en la moneda principal) y neutral (todo el resto — evita saturar de naranja).
 */
function CurrencyChip({ code, size = 'md', tone = 'neutral' }) {
  const sizes = {
    lg: 'w-14 h-14 text-[15px] rounded-2xl',
    md: 'w-10 h-10 text-[12px] rounded-xl',
  };
  const tones = {
    primary:
      'bg-gradient-to-br from-primary/15 to-primary/[0.04] ' +
      'dark:from-primary/25 dark:to-primary/[0.05] ' +
      'border border-primary/25 dark:border-primary/35 text-primary ' +
      'shadow-[0_1px_0_rgba(255,255,255,0.4)_inset] dark:shadow-[0_1px_0_rgba(255,255,255,0.05)_inset]',
    neutral:
      'bg-white dark:bg-white/[0.04] ' +
      'border border-black/[0.06] dark:border-white/[0.06] ' +
      'text-foreground/80 dark:text-foreground/90 ' +
      'shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_1px_2px_rgba(24,15,3,0.03)] ' +
      'dark:shadow-[0_1px_0_rgba(255,255,255,0.03)_inset]',
  };
  return (
    <div
      className={`${sizes[size]} ${tones[tone]} flex items-center justify-center flex-shrink-0 font-bold tracking-tight`}
      style={{ fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' }}
      aria-label={`Moneda ${code}`}
    >
      <span className="leading-none">{code}</span>
    </div>
  );
}

function SkeletonHero() {
  return (
    <div className="p-6 rounded-[22px] bg-surface-2 border border-border animate-pulse flex items-center gap-5">
      <div className="w-14 h-14 rounded-2xl bg-muted" />
      <div className="flex-1 space-y-2.5">
        <div className="h-2.5 bg-muted rounded w-1/4" />
        <div className="h-4 bg-muted rounded w-1/2" />
      </div>
    </div>
  );
}

function ConfirmPrimaryChange({ code, name, previous, busy, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="confirm-primary-modal"
    >
      <div
        className="w-full max-w-md bg-surface dark:bg-[#12141c] border border-black/[0.06] dark:border-white/[0.08]
                   rounded-[22px] shadow-[0_20px_50px_-16px_rgba(24,15,3,0.20)] dark:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]
                   p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-100/80 dark:bg-amber-500/15 border border-amber-300/60 dark:border-amber-400/25 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" strokeWidth={2} />
          </div>
          <h3 className="text-[17px] font-semibold text-foreground tracking-tight">Cambiar moneda principal</h3>
        </div>

        <div className="text-[13px] leading-relaxed text-muted-foreground space-y-2.5 mb-6">
          <p>
            Vas a pasar de{' '}
            <span className="font-semibold text-foreground tabular-nums">{previous}</span> a{' '}
            <span className="font-semibold text-foreground tabular-nums">{code}</span>{' '}
            <span className="text-muted-foreground/80">— {name}</span>.
          </p>
          <p>
            Los <span className="font-medium text-foreground">importes originales de tus gastos históricos no cambian</span>.
            La nueva moneda principal se usa sólo para los futuros cálculos de conversión y los totales del dashboard.
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-5 py-2.5 rounded-full text-[13px] font-medium text-foreground/70 hover:bg-black/[0.04] dark:hover:bg-white/[0.05] disabled:opacity-40 transition-colors"
            data-testid="confirm-primary-cancel-btn"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="px-5 py-2.5 rounded-full text-[13px] font-semibold text-white
                       bg-gradient-to-b from-primary to-[#EA580C] hover:brightness-105
                       shadow-[0_1px_0_rgba(255,255,255,0.25)_inset,0_6px_16px_-4px_rgba(249,115,22,0.45)]
                       disabled:opacity-60 flex items-center gap-2 transition"
            data-testid="confirm-primary-apply-btn"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
            Sí, cambiar
          </button>
        </div>
      </div>
    </div>
  );
}
